/**
 * frontend/src/stores/themeStore.ts
 * `#1060` — LE THÈME (SOMBRE | CLAIR), PERSISTÉ, APPLIQUÉ SANS RECHARGEMENT.
 *
 * ⚠⚠ Décision Hkm (17/09/2026) : **défaut SOMBRE**, mémorisé par navigateur.
 *   Renverse `#899` (« on zappe »), cf. `#1059`.
 *
 * ⚠ La clé persistée `orbicore-theme` porte la forme zustand `{ state: { theme } }`
 *   — c'est EXACTEMENT ce que lit l'anti-flash de `index.html` AVANT React, pour
 *   éviter le flash de thème à l'ouverture. Les deux DOIVENT rester d'accord sur
 *   le nom de la clé, la forme, et le défaut (`dark`).
 * ⚠ `appliquerTheme` mute `documentElement` (classe `.dark`/`.light` + attribut
 *   `data-theme`) : les tokens CSS se recalculent instantanément, aucun re-render
 *   ni rechargement nécessaire — c'est le mécanisme dual de `tailwind.config.ts`.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'dark' | 'light';

/** Applique le thème au DOM (sans rechargement). Idempotent. */
export function appliquerTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  root.classList.toggle('light', theme === 'light');
  root.setAttribute('data-theme', theme);
}

interface ThemeState {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggleTheme: () => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'dark', // défaut Hkm : sombre
      setTheme: (theme) => {
        appliquerTheme(theme);
        set({ theme });
      },
      toggleTheme: () => {
        const next: Theme = get().theme === 'dark' ? 'light' : 'dark';
        appliquerTheme(next);
        set({ theme: next });
      },
    }),
    {
      name: 'orbicore-theme',
      // ⚠ Après réhydratation depuis localStorage, on ré-applique — filet si
      //   l'anti-flash n'a pas tourné (SSR, tests, navigation directe).
      onRehydrateStorage: () => (state) => {
        if (state) appliquerTheme(state.theme);
      },
    },
  ),
);
